import _, {findLastKey} from 'lodash';
import {connect} from 'react-redux';

import {MessageTypesEnum} from '../../../../../libs/shared/core/src/models/api/commonMessageModel';
import {MessageVisualStatusesEnum} from '../../../../../libs/shared/core/src/models/api/messageModel';
import {RecipientRolesEnum} from '../../../../../libs/shared/core/src/models/api/recipientModel';
import {
  CalendarsPanelId,
  tryFindDockedViewerPanelId,
} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {
  AppParametricSelector,
  createSelector,
  makeMapStateToProps,
} from '../../../../../srcCommon/helpers/reselectHelpers';
import {buildVisualParticipantFromMatch} from '../../../../helpers/models/participantHelpers';
import {ActivityVisualTypesEnum} from '../../../../models/api/activityModel';
import {VisualParticipantModel} from '../../../../models/api/visualParticipantModel';
import {fetchRecipientContactMatchesAsync} from '../../../../services/recipientMatches';
import {AppState} from '../../../../store/appState';
import {getCompany} from '../../../../store/data/company/companySelectors';
import {getFindTeammateAndOnlineStatusForVisualContact} from '../../../../store/data/contacts/contactsSelectors';
import {getBuildVisualParticipantFromRecipient} from '../../../../store/data/contacts/participantsSelectors';
import {getActivities, getMessages} from '../../../../store/data/dataSelectors';
import {getUserNamespace} from '../../../../store/data/teammates/teammatesSelectors';
import {makeGetContentPage, makeGetViewerPanel} from '../../../../store/panels/viewer/viewerPanelSelectors';
import {
  ViewerPanelPageState,
  ViewerPanelPageTypesEnum,
} from '../../../../store/panels/viewer/viewerPanelState';
import {DropdownFetcher} from '../../../common/dropdowns/source/asyncDropdownSource';
import {VisualParticipantsDropdown} from './visualParticipantsDropdown';

/*
 * Constants.
 */

const resultsLimit = 5;

/*
 * Props.
 */

interface VisualParticipantsDropdownContainerProps {
  panelId: CalendarsPanelId;
  query: string;
  hasExistingAttendees: boolean;
  isUnsavedDraft: boolean;
  organizerAddress?: string;
}

/*
 * Selectors.
 */

function getQuery(_state: AppState, props: VisualParticipantsDropdownContainerProps) {
  return props.query;
}

function getHasExistingAttendees(_state: AppState, props: VisualParticipantsDropdownContainerProps) {
  return props.hasExistingAttendees;
}

function getIsUnsavedDraft(_state: AppState, props: VisualParticipantsDropdownContainerProps) {
  return props.isUnsavedDraft;
}

function getOrganizerAddress(_state: AppState, props: VisualParticipantsDropdownContainerProps) {
  return props.organizerAddress;
}

type ViewerContentPageSelector = AppParametricSelector<
  VisualParticipantsDropdownContainerProps,
  ViewerPanelPageState | undefined
>;

function makeGetParentViewerContentPage(): ViewerContentPageSelector {
  const getViewerPanel = makeGetViewerPanel();
  const getContentPage = makeGetContentPage(getViewerPanel);

  return (state, props) => {
    const panelId = tryFindDockedViewerPanelId(props.panelId);
    if (!panelId) {
      return undefined;
    }

    return getContentPage(state, {panelId});
  };
}

function makeGetParticipantsFromCurrentTimelineMessage() {
  return createSelector(
    [
      makeGetParentViewerContentPage(),
      getMessages,
      getActivities,
      getBuildVisualParticipantFromRecipient,
      getFindTeammateAndOnlineStatusForVisualContact,
      getOrganizerAddress,
      getQuery,
      getHasExistingAttendees,
      getIsUnsavedDraft,
    ],
    (
      parentViewerContentPage,
      messages,
      activities,
      buildVisualParticipantFromRecipient,
      findTeammateAndOnlineStatusForVisualContact,
      organizerAddress,
      query,
      hasExistingAttendees,
      isUnsavedDraft,
    ) => {
      // Only suggest participants if these conditions are satisfied:
      // 1. The user is creating a new event.
      // 2. There is no query typed in the input box.
      // 3. There is no existing attendee.
      // 4. We're dealing with a side panel calendar.
      if (
        !isUnsavedDraft ||
        query ||
        hasExistingAttendees ||
        parentViewerContentPage?.type !== ViewerPanelPageTypesEnum.SINGLE_CONVERSATION
      ) {
        return undefined;
      }

      const lastMessageActivityId = findLastKey(
        parentViewerContentPage.activities,
        (activity) => activity && activity.type === ActivityVisualTypesEnum.MESSAGE && activity.isExpanded,
      );

      const messageActivity = lastMessageActivityId && activities[lastMessageActivityId];
      if (!messageActivity || messageActivity.visualType !== ActivityVisualTypesEnum.MESSAGE) {
        return undefined;
      }

      const message = messages[messageActivity.messageUid];
      if (
        !message ||
        message.visualStatus !== MessageVisualStatusesEnum.TIMELINE ||
        message.type !== MessageTypesEnum.EMAIL
      ) {
        return undefined;
      }

      const participants = _(message.recipients)
        .uniqBy((r) => r.contactUid)
        .filter((r) =>
          [RecipientRolesEnum.TO, RecipientRolesEnum.CC, RecipientRolesEnum.FROM].includes(r.role),
        )
        .map((r) => {
          const visualParticipant = buildVisualParticipantFromRecipient(r);
          // Do not return the organizer as a suggested participant.
          if (!visualParticipant || visualParticipant.contact.handle === organizerAddress) {
            return undefined;
          }

          const contactWithTeammateAndOnlineStatus =
            findTeammateAndOnlineStatusForVisualContact(visualParticipant);
          const vp: VisualParticipantModel = {
            ...visualParticipant,
            ...contactWithTeammateAndOnlineStatus,
          };

          return vp;
        })
        .compact()
        .value();

      const maxSuggestedParticipantCount = 6;

      return participants.length <= maxSuggestedParticipantCount ? participants : undefined;
    },
  );
}

function makeGetFetcher() {
  return createSelector(
    [getCompany, getUserNamespace, getFindTeammateAndOnlineStatusForVisualContact],
    (
        company,
        namespace,
        findTeammateAndOnlineStatusForVisualContact,
      ): DropdownFetcher<VisualParticipantModel> =>
      async (query, _items, cancelToken) => {
        if (!query) {
          return {items: [], canLoadMore: false};
        }

        const options = {cancelToken, preferredNamespace: namespace};
        const matches = await fetchRecipientContactMatchesAsync(company.url, query, options);
        const allVisualParticipants = _(matches)
          .map((m) => {
            const visualParticipant = buildVisualParticipantFromMatch(namespace, m);
            const contactWithTeammateAndOnlineStatus =
              findTeammateAndOnlineStatusForVisualContact(visualParticipant);

            const vp: VisualParticipantModel = {
              ...visualParticipant,
              ...contactWithTeammateAndOnlineStatus,
            };

            return vp;
          })
          .compact()
          .value();

        return {
          items: _(allVisualParticipants)
            .uniqBy((r) => r.contact.uid)
            .take(resultsLimit)
            .value(),
          canLoadMore: false,
        };
      },
  );
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<VisualParticipantsDropdownContainerProps>()(() => ({
  fetcher: makeGetFetcher(),
  suggestedParticipants: makeGetParticipantsFromCurrentTimelineMessage(),
}));

export const VisualParticipantsDropdownContainer = connect(mapStateToProps)(VisualParticipantsDropdown);
